'use client';

import { Locale } from '@/i18n/i18nConfig.types';
import { useOfferToolUser } from '@/providers/OfferToolUserProvider/OfferToolUserProvider';
import { useUser as useAuth0User } from '@auth0/nextjs-auth0/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocale } from 'next-intl';
import { useEffect, useRef } from 'react';
import userflow from 'userflow.js';

const USERFLOW_TOKEN = process.env.NEXT_PUBLIC_USERFLOW_TOKEN;

const mapLocaleToUserflowLocale = (locale: string): string => {
  const localeMap: Record<Locale, string> = {
    [Locale.de]: 'de-DE',
    [Locale.en]: 'en-US',
    [Locale.sv]: 'sv-SE',
    [Locale.da]: 'da-DK',
    [Locale.nl]: 'nl-NL',
  };

  return localeMap[locale as Locale] || localeMap.en;
};

export const UserflowProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user: auth0User } = useAuth0User();
  const { user: offerToolUser, getCountry } = useOfferToolUser();
  const locale = useLocale();
  const featureFlags = useFlags();
  const isInitializedRef = useRef(false);
  const lastUserIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (isInitializedRef.current) {
      return;
    }

    if (!USERFLOW_TOKEN) {
      return;
    }

    try {
      userflow.init(USERFLOW_TOKEN);
      isInitializedRef.current = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize Userflow:', error);
    }
  }, []);

  // Identify user when app user data is available
  // Note: offerToolUser only exists when auth0User is available (OfferToolUserProvider waits for it)
  useEffect(() => {
    if (!isInitializedRef.current || !offerToolUser) {
      return;
    }

    const userId = String(offerToolUser.id);
    const userName = offerToolUser.name;
    const userEmail = offerToolUser.email;
    const userflowLocale = mapLocaleToUserflowLocale(locale);
    const country = getCountry();

    // Get active feature flags (only those that are enabled)
    const activeFeatureFlags = Object.entries(featureFlags)
      .filter(([, value]) => value === true)
      .map(([key]) => key);

    // Only identify if user changed
    if (lastUserIdRef.current === userId) {
      return;
    }

    try {
      /* eslint-disable-next-line @typescript-eslint/no-floating-promises */
      userflow.identify(userId, {
        name: userName,
        email: userEmail,
        locale_code: userflowLocale,
        country,
        active_feature_flags: activeFeatureFlags,
      });

      lastUserIdRef.current = userId;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to identify user in Userflow:', error);
    }
  }, [offerToolUser, locale, featureFlags, getCountry]);

  // Reset identification when user logs out
  useEffect(() => {
    if (!isInitializedRef.current) {
      return;
    }

    if (!auth0User && lastUserIdRef.current) {
      try {
        userflow.reset();
        lastUserIdRef.current = undefined;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to reset Userflow:', error);
      }
    }
  }, [auth0User]);

  return <>{children}</>;
};
